import { linkWithGoogle, unlinkGoogle } from '@/services/AuthService';
import { BraindayAPI, BraindayError } from '@/services/BraindayAPI';
import { useUIStore } from '@/stores/uiStore';
import { useCalendarStore } from '@/stores/calendarStore';
import { uuid } from '@/lib/uuid';
import { fromDateKey } from '@/lib/dateKey';
import type { CalendarEvent } from '@/types';
import type { WireCalendarEvent, ProbeCalendarOutput } from '@/lib/calendarClient';

function toCalendarEvent(w: WireCalendarEvent): CalendarEvent {
  // All-day events arrive as bare calendar dates and must anchor to LOCAL
  // midnight of that day — the startMs/endMs fallbacks are UTC midnight,
  // which is the previous local day anywhere west of UTC. Resolution happens
  // at sync time, and every native resume re-syncs, so a timezone change is
  // corrected on the first app open after landing.
  const start =
    (w.isAllDay && w.startDate ? fromDateKey(w.startDate) : null) ?? new Date(w.startMs);
  const end = (w.isAllDay && w.endDate ? fromDateKey(w.endDate) : null) ?? new Date(w.endMs);
  return {
    id: uuid(),
    externalId: w.externalId,
    source: 'google',
    title: w.title,
    start,
    end,
    isAllDay: w.isAllDay,
    ...(w.location ? { location: w.location } : {}),
    ...(w.color ? { color: w.color } : {}),
    ...(w.meetingUrl ? { meetingUrl: w.meetingUrl } : {}),
    isReadOnly: true,
  };
}

function persistEvents(wires: WireCalendarEvent[]): void {
  const events = wires.map(toCalendarEvent);
  useCalendarStore.getState().setEvents(events);
}

async function reconcileAndPersist(): Promise<{ syncedAt: Date; count: number }> {
  try {
    const out = await BraindayAPI.reconcileCalendar();
    useUIStore.getState().setGoogleSyncBroken(undefined);
    persistEvents(out.events);
    return { syncedAt: new Date(out.syncedAtMs), count: out.events.length };
  } catch (e) {
    // A dead or missing refresh token is not transient — record it so the UI
    // can show a persistent Reconnect prompt instead of "Connected ✓". This
    // runs on background syncs too, whose errors are otherwise swallowed.
    if (e instanceof BraindayError && (e.code === 'token-expired' || e.code === 'not-connected')) {
      useUIStore.getState().setGoogleSyncBroken(e.code);
    }
    throw e;
  }
}

async function revokeAccessToken(token: string): Promise<void> {
  // Best-effort — revoke endpoint must succeed eventually, but a transient
  // failure here should not block the rest of the disconnect flow. The
  // refresh-token doc gets deleted regardless, so a leftover live access
  // token will expire on its own within 1h.
  await fetch(`https://oauth2.googleapis.com/revoke?token=${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  }).catch(() => undefined);
}

export const GoogleCalendarService = {
  async connect(): Promise<{ syncedAt: Date; count: number }> {
    const linked = await linkWithGoogle();
    // Store the refresh token server-side BEFORE flipping local state to
    // connected — the reverse order left a permanent "Connected ✓" with no
    // server-side token when this call failed (nothing could ever sync).
    if (linked.refreshToken) {
      await BraindayAPI.storeGoogleRefresh(linked.refreshToken, linked.mintedBy ?? 'web');
    }
    useUIStore
      .getState()
      .setGoogleAccessToken(
        linked.accessToken,
        new Date(Date.now() + linked.expiresInSec * 1000),
        linked.email,
      );
    return reconcileAndPersist();
  },

  async sync(): Promise<{ syncedAt: Date; count: number }> {
    return reconcileAndPersist();
  },

  async probe(): Promise<ProbeCalendarOutput> {
    return BraindayAPI.probeCalendar();
  },

  async disconnect(): Promise<void> {
    const accessToken = useUIStore.getState().googleAccessToken;
    if (accessToken) await revokeAccessToken(accessToken);

    // Server-side: revoke the refresh token at Google (best-effort) + delete the
    // Firestore integration doc. Replaces the previous client-side deleteDoc()
    // because the client cannot decrypt the refresh token to revoke it.
    try {
      await BraindayAPI.revokeGoogleRefresh();
    } catch {
      // Best-effort. If the server call fails (offline, transient error), we
      // still clear local state below so the UI returns to Disconnected.
    }

    useUIStore.getState().clearGoogleAccessToken();
    useCalendarStore.getState().clearForSource('google');
    try {
      await unlinkGoogle();
    } catch {
      // Best-effort: on web `signInWithGoogle()` doesn't actually link Google
      // to the Firebase user, so `unlink({providerId: GOOGLE})` throws. The
      // local store is already cleared above — that's what drives the UI.
    }
  },
};
